/**
 * PLの1行(表示用モデル)。現行「PL日報」の「1日」シートの行に対応する。
 * PlPage(集計) と PlSheetView / PlTreeView(描画) の両方から使うため独立させている。
 */
import { formatCount, formatCurrency, formatPercent, formatScaledCurrency, type MoneyScale } from '@core/utils/formatters';

export type Fmt = 'int' | 'yen' | 'pct' | 'price' | 'ratio';

/** PL表の1行。values は会社ID(または期間キー) → 値(null=空欄)。total は合計列 */
export type PlRow = {
  key: string;
  label: string;
  fmt: Fmt;
  values: Record<string, number | null>;
  total: number | null;
  /** 行の意味(カーソルで吹き出し) */
  hint?: string;
  better?: string;
  bold?: boolean;
  section?: boolean; // 区分の見出し行
  children?: PlRow[];
  level?: number;
  /** 合計列に別の表示(粗利率など) */
  totalExtra?: string;
  /** 原価率の帯色を付ける */
  colorRate?: boolean;
  /** 親階層の色分け(PL階層版。子・孫にも引き継ぐ。2026-09-10 指示) */
  tone?: string;
  /** 小計・合計・率の行(①シート版では行まるごと大分類の色。2026-09-11 指示の見本) */
  summary?: boolean;
};

/**
 * 科目のまとまり(行の色分け)。①シート版と②階層版で同じ科目に同じ色を使う(2026-09-11 指示)。
 * appt=アポ数 / sales=売上 / cost=売上原価 / gross=粗利 / exp=経費 / op=営業利益 / nonop=営業外経費 / ord=経常利益
 * 2026-09-18 UIデザインシステム(D-90)で色分けはやめ、①シート版の大分類の列の名前と、まとまりの区切り線に使う
 */
export const TONE_LABELS: Record<string, string> = {
  appt: 'アポ数',
  sales: '売上',
  cost: '売上原価',
  gross: '粗利',
  exp: '経費',
  op: '営業利益',
  nonsales: '営業外売上',
  nonop: '営業外経費',
  ord: '経常利益',
};

export function toneOf(key: string): string {
  if (key === 'ordinary') return 'ord';
  if (key === 'op_profit') return 'op';
  if (key.startsWith('sec_nonop') || key.startsWith('mgr_bonus') || key.startsWith('nonop')) return 'nonop';
  if (key.startsWith('sec_nonsales') || key.startsWith('nonsales')) return 'nonsales';
  if (key.startsWith('sec_exp') || key.startsWith('exp_')) return 'exp'; // expected(売上見込)は売上側なので exp_ で見る
  if (key.startsWith('gross')) return 'gross';
  if (key.startsWith('sec_cost') || key.startsWith('cost')) return 'cost';
  if (key.startsWith('sec_sales') || key.startsWith('sales') || key === 'unit' || key === 'expected' || key === 'cancel') return 'sales';
  return 'appt'; // 構成比・アポ数(目標/取得数)・訪問数
}

/** 金額の単位(PLの表示切替。2026-09-10 指示) */
export type MoneyUnit = MoneyScale;
export const MONEY_UNIT_LABELS: Record<MoneyUnit, string> = { yen: '円', k: '千円', man: '万円' };

/**
 * 金額を単位に合わせて表示する。件数・率はそのまま。
 * 2026-09-18 UIデザインシステムの数値の書き方に統一(D-90): 円=¥1,234 / 千円・万円=小数第1位 / マイナスは「−」/ 値なしは「—」/ 率は小数第1位
 */
export function fmtCellUnit(v: number | null, fmt: Fmt, unit: MoneyUnit): string {
  if (fmt === 'yen' || fmt === 'price') return formatScaledCurrency(v, unit);
  return fmtCell(v, fmt);
}

export function fmtCell(v: number | null, fmt: Fmt): string {
  switch (fmt) {
    case 'int':
      return formatCount(v);
    case 'yen':
    case 'price':
      return formatCurrency(v);
    case 'pct':
    case 'ratio':
      return formatPercent(v);
  }
}

/** 展開できる行のキー(すべて開く/閉じる用) */
export function collectKeys(rows: PlRow[]): string[] {
  return rows.flatMap((r) => [...((r.children?.length ?? 0) > 0 ? [r.key] : []), ...collectKeys(r.children ?? [])]);
}
